import React, { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "react-hot-toast";
import MoonLoader from "react-spinners/MoonLoader";
import clsx from "clsx";
import { useSearchParams } from "react-router-dom";
import { IoMdAddCircle } from "react-icons/io";
import { PiPencilSimpleLine } from "react-icons/pi";
import { useAuth0 } from "@auth0/auth0-react";
import CustomTooltip from "../../../../components/common/CustomTooltip";
import { Helmet } from "react-helmet";

import SinglePropertyFeed from "./SinglePropertyFeed/SinglePropertyFeed";
import CustomFeed from "./CustomFeed/CustomFeed";
import SplashModal from "./SplashModal";
import NickNameModal from "./NickNameModal";

import { getListingFeedProperties, getPropertyByLinkCode } from "../../../../services/PropertyService";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import { useInfiniteQuery, useQueryClient, useQuery } from "@tanstack/react-query";
import { useUser } from "../../../../context/UserContext";
import useCustomLoginWithRedirect from "../../../../hooks/useCustomLoginWithRedirect";
import { THIRTY_MINUTES } from "../../../../constants/time";
import { SOCIAL_FEED_TITLE } from "../../../../constants/social-feed-title";
import { syncUser } from "../../../../services/UserService";
import PropertyFeedSkeleton from "./PropertyFeedSkeleton";
import { getUserFeeds } from "../../../../services/UserFeedService";
import { useFeedAnimation } from "../../../../context/FeedAnimationContext";
import SuccessAnimation from "./CustomFeed/SuccessAnimation";
import DeleteSuccessAnimation from "./CustomFeed/components/DeleteSuccessAnimation";

const INITIAL_LIMIT = 10;
const CACHE_TIME = THIRTY_MINUTES; // 30 minutes
const SCROLL_TRANSITION_DISTANCE = 300; // Adjusted distance for balanced unshrinking
const INITIAL_FONT_SIZE = 18; // Initial font size in pixels
const SHRUNK_FONT_SIZE = 16; // Font size when scrolled
const INITIAL_PADDING = 0.75; // Increased initial padding in rem
const SHRUNK_PADDING = 0.5; // Increased padding when scrolled
const INITIAL_TOP_PADDING = 3.5; // Increased initial top padding in rem
const SHRUNK_TOP_PADDING = 2.5; // Increased top padding when scrolled

const SPLASH_TIMER_DURATION = 4500;

const getInitialSplashState = (isAuthenticated) => {
    // Only show splash screen for unauthenticated users
    if (!isAuthenticated) {
        return !localStorage.getItem("splashSeenGuest");
    }
    return false;
};

/**
 * ListingFeed component displays List of properties so user can interact with them
 * @returns {JSX.Element} -  List of Properties and Filter Selection section.
 */
const ListingFeed = () => {
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    const [showSplash, setShowSplash] = useState(() => getInitialSplashState(isAuthenticated));
    const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const [searchParams] = useSearchParams();
    const [socialState, setSocialState] = useLocalStorage("social-state");
    const [zipcode, setZipCode] = useState("60657");
    const [splashTimerDone, setSplashTimerDone] = useState(false);
    const [feedLoaded, setFeedLoaded] = useState(false);
    const [sortBy, setSortBy] = useState("Trending");
    const [sortByValue, setSortByValue] = useState("trending");
    const [scrollProgress, setScrollProgress] = useState(0);
    const [filters, setFilters] = useState({
        bedrooms: ["1", "2", "3", "4", "5+"],
        bathrooms: ["1", "2", "3", "4+"],
        propertyType: ["condo", "house"],
        priceRange: { min: 0, max: 0 },
    });
    const [activeTab, setActiveTab] = useState("trending");
    const [showFeedDrawer, setShowFeedDrawer] = useState(false);
    const lastScrollY = useRef(0);
    const [activeTooltip, setActiveTooltip] = useState(null); // 'trending', 'see-more', 'following'
    const tooltipTimeoutRef = useRef(null);
    const firstCardRef = useRef(null);
    const [showNicknameModal, setShowNicknameModal] = useState(false);
    const queryClient = useQueryClient();
    const loaderRef = useRef(null);
    const { showSuccessAnimation, showDeleteSuccessAnimation, deleteMessage, hideAnimations } =
        useFeedAnimation();

    // Fetch user feeds to determine if user has any feeds
    const { data: feedsResponse = { data: [] } } = useQuery({
        queryKey: ["userFeeds", userState?.id],
        queryFn: () => getUserFeeds(userState?.id),
        enabled: !!userState?.id,
        refetchOnWindowFocus: true,
        staleTime: 0,
    });
    const feeds = feedsResponse?.data || [];
    const hasFeeds = Array.isArray(feeds) && feeds.length > 0;

    // Extract plc parameter from URL
    const plc = searchParams.get("plc");

    // Query for top property if plc parameter exists
    const { data: topPropertyData, error: topPropertyError } = useQuery({
        queryKey: ["top-property", plc],
        queryFn: () => getPropertyByLinkCode({ property_link_code: plc }),
        enabled: !!plc,
        staleTime: THIRTY_MINUTES,
        cacheTime: THIRTY_MINUTES,
    });

    // Handle top property error
    useEffect(() => {
        if (topPropertyError) {
            console.error("Error fetching top property:", topPropertyError);
            toast.error("Could not load the specific property. Showing trending properties instead.");
        }
    }, [topPropertyError]);

    // Extract common button styles
    const tabButtonStyles = {
        fontSize: `${INITIAL_FONT_SIZE - (INITIAL_FONT_SIZE - SHRUNK_FONT_SIZE) * scrollProgress}px`,
        paddingTop: `${INITIAL_PADDING - (INITIAL_PADDING - SHRUNK_PADDING) * scrollProgress}rem`,
        paddingBottom: `${INITIAL_PADDING - (INITIAL_PADDING - SHRUNK_PADDING) * scrollProgress}rem`,
    };

    useEffect(() => {
        const scrollY = socialState?.lastScrollPosition;

        if (socialState?.activeTab) {
            setActiveTab(socialState?.activeTab);
        }

        if (scrollY) {
            setTimeout(() => {
                window.scrollTo({ top: scrollY, behavior: "instant" });
            }, 100); // small delay ensures DOM is ready
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const loginParam = searchParams.get("login");
        if (loginParam === "true" && !isAuthenticated) {
            // Show toast first
            toast("Please login to continue", {
                id: "login-required-toast", // This ensures only one toast appears at a time
                duration: 3000,
                position: "top-center",
            });

            // Remove the login parameter from URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete("login");
            window.history.replaceState(
                {},
                "",
                `${window.location.pathname}${newSearchParams.toString() ? "?" + newSearchParams.toString() : ""}`
            );

            // Small delay before redirect to ensure toast is visible
            setTimeout(() => {
                customLoginWithRedirect({
                    authorizationParams: {
                        redirect_uri: window.location.origin + "/social",
                    },
                    appState: {
                        version: "social",
                    },
                });
            }, 500);
        }
    }, [searchParams, isAuthenticated, customLoginWithRedirect]);

    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: [
            "live-variant-listingFeed-properties",
            zipcode,
            sortByValue,
            filters.propertyType,
            filters.priceRange.min,
            filters.priceRange.max,
            filters.bedrooms,
            filters.bathrooms,
            userState?.id,
        ],
        queryFn: ({ pageParam = 1 }) =>
            getListingFeedProperties({
                sort: sortByValue,
                propertyType: filters.propertyType,
                bedrooms: filters.bedrooms,
                bathrooms: filters.bathrooms,
                page: pageParam.toString(),
                limit: INITIAL_LIMIT.toString(),
                userId: userState?.id,
                feedType: "trending",
            }),
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage?.data?.hasNextPage) {
                return allPages.length + 1;
            }
            return undefined;
        },
        staleTime: 0,
        cacheTime: CACHE_TIME,
        onError: (error) => {
            if (error?.response?.status === 500 || error?.response?.status === 400) {
                toast.error("Currently, There are no properties. Please try again later.");
            }
        },
    });

    useEffect(() => {
        if (socialState && socialState.zipcode && socialState.sortBy && socialState.filters) {
            setZipCode(socialState.zipcode);
            setFilters(socialState.filters);
            if (socialState.sortBy === "price-high-low") {
                setSortBy("Price: High to Low");
                setSortByValue("price-high-low");
            } else if (socialState.sortBy === "price-low-high") {
                setSortBy("Price: Low to High");
                setSortByValue("price-low-high");
            } else {
                setSortBy("Trending");
                setSortByValue("trending");
            }
        } else {
            setSocialState({
                zipcode,
                sortBy,
                filters,
            });
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 1.0 }
        );

        const currentLoader = loaderRef.current;
        if (currentLoader) observer.observe(currentLoader);
        return () => {
            if (currentLoader) observer.unobserve(currentLoader);
        };
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    const currentScrollY = window.scrollY;
                    lastScrollY.current = currentScrollY;

                    // Calculate progress based on current scroll position
                    const progress = Math.min(currentScrollY / SCROLL_TRANSITION_DISTANCE, 1);

                    setScrollProgress(progress);
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleSplashClose = useCallback(() => {
        localStorage.setItem("splashSeenRegistered", "true");
        localStorage.setItem("splashSeenGuest", "true");
        setShowSplash(false);
    }, []);

    useEffect(() => {
        try {
            if (!localStorage.getItem("splashSeenGuest") && !localStorage.getItem("splashSeenRegistered")) {
                setShowSplash(true);
            }
        } catch (error) {
            console.error("Error accessing localStorage:", error);
        }

        if (showSplash && splashTimerDone && !isLoading) {
            handleSplashClose();
        }
        const timer = setTimeout(() => setSplashTimerDone(true), SPLASH_TIMER_DURATION);

        return () => clearTimeout(timer);
    }, [showSplash, splashTimerDone, isLoading, handleSplashClose]);

    useEffect(() => {
        if (!showSplash) {
            // Check for property detail click when component mounts or when returning to trending page
            const propertyDetailClicked = localStorage.getItem("property_detail_clicked");

            if (propertyDetailClicked === "true") {
                // If a property was clicked, show tooltips based on conditions
                if (!localStorage.getItem("trendingTooltipSeen")) {
                    setActiveTooltip("trending");
                } else if (!localStorage.getItem("seeMoreTooltipSeen")) {
                    setActiveTooltip("see-more");
                } else if (!localStorage.getItem("followingTooltipSeen")) {
                    setActiveTooltip("following");
                } else {
                    setActiveTooltip(null);
                }
                // Clear the flag so it doesn't show again until next property click
                localStorage.removeItem("property_detail_clicked");
            } else {
                if (!localStorage.getItem("trendingTooltipSeen")) {
                    setActiveTooltip("trending");
                    localStorage.setItem("trendingTooltipSeen", "true");
                }
            }
        }
    }, [showSplash, activeTab]);

    // Clean up any tooltip timeout on unmount
    useEffect(() => {
        return () => {
            if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
                tooltipTimeoutRef.current = null;
            }
        };
    }, []);

    // Combine top property with trending properties
    const combinedProperties = React.useMemo(() => {
        const allProperties = data?.pages ? data.pages.flatMap((page) => page.data?.data) : [];

        if (topPropertyData?.data && activeTab === "trending") {
            // Filter out properties with the same ID as the top property to prevent duplication
            const filteredProperties = allProperties.filter(
                (property) => property.id !== topPropertyData.data.id
            );
            // Add top property as the first property in the list
            return [topPropertyData.data, ...filteredProperties];
        }
        return allProperties;
    }, [topPropertyData?.data, data?.pages, activeTab]);

    useEffect(() => {
        if (isAuthenticated && localStorage.getItem("showSetNicknameModal") === "true") {
            setShowNicknameModal(true);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (combinedProperties.length > 0) {
            setFeedLoaded(true);
        }
    }, [combinedProperties.length]);

    const handleTrendingTooltipClose = () => {
        // When trending closes, immediately show see-more if not already seen
        if (!localStorage.getItem("seeMoreTooltipSeen") && combinedProperties.length > 0) {
            if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
            }
            tooltipTimeoutRef.current = setTimeout(() => {
                setActiveTooltip("see-more");
            }, 1000);
        } else {
            setActiveTooltip(null);
        }
    };

    const handleSeeMoreTooltipClose = () => {
        setActiveTooltip(null);
        localStorage.setItem("seeMoreTooltipSeen", "true");
        // After see-more closes, wait 5s, then show following-tooltip if not already seen and still on trending
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
        }
        tooltipTimeoutRef.current = setTimeout(() => {
            try {
                const followingTooltipSeen = localStorage.getItem("followingTooltipSeen");
                const seeMoreTooltipSeen = localStorage.getItem("seeMoreTooltipSeen");

                if (activeTab === "trending" && !followingTooltipSeen && seeMoreTooltipSeen === "true") {
                    setActiveTooltip("following");
                }
            } catch (error) {
                console.error("Error accessing localStorage:", error);
            }
        }, 5000);
    };

    const handleFollowingTooltipClose = () => {
        setActiveTooltip(null);
        localStorage.setItem("followingTooltipSeen", "true");
    };

    const handleSaveNickname = async (newNickname) => {
        try {
            const payload = {
                id: userState?.id,
                nickname: newNickname,
                email: userState?.email_address,
            };

            await syncUser(payload);
            await queryClient.invalidateQueries(["user", userState?.id]);
            setShowNicknameModal(false);
            localStorage.setItem("showSetNicknameModal", "false");
            toast.success("Nickname updated successfully!");
        } catch (error) {
            toast.error(error?.response?.data?.message ?? "Oops... something went wrong.");
        }
    };

    useEffect(() => {
        if (showSplash && splashTimerDone && feedLoaded) {
            handleSplashClose();
        }
    }, [showSplash, splashTimerDone, feedLoaded, handleSplashClose]);

    if (showSplash) {
        return <SplashModal onClose={handleSplashClose} />;
    }

    const ScrollBackToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <>
            <Helmet>
                <title>{SOCIAL_FEED_TITLE}</title>
            </Helmet>
            {showNicknameModal && (
                <NickNameModal
                    nickname={userState?.nickname}
                    onSave={(newNickname) => handleSaveNickname(newNickname)}
                    onClose={() => {
                        localStorage.setItem("showSetNicknameModal", "false");
                        setShowNicknameModal(false);
                    }}
                />
            )}

            <div
                className="property-container tw-relative tw-flex tw-w-full md:tw-w-[600px] tw-flex-col tw-justify-self-center tw-mx-auto"
                style={{
                    paddingTop: `${INITIAL_TOP_PADDING - (INITIAL_TOP_PADDING - SHRUNK_TOP_PADDING) * scrollProgress}rem`,
                }}
            >
                {/* Tab Bar */}
                <div
                    className={clsx(
                        "tw-flex tw-mb-2 tw-border-b tw-border-[#E5E5E5] tw-bg-white",
                        "tw-fixed tw-left-1/2 tw-transform -tw-translate-x-1/2 tw-w-full md:tw-max-w-[600px] tw-z-50 tw-top-[9svh]",
                        "tw-transition-all tw-duration-200 tw-ease-out",
                        scrollProgress > 0 ? "tw-shadow-sm" : ""
                    )}
                >
                    <button
                        className={clsx(
                            "tw-flex-1 tw-text-center tw-font-bold tw-bg-white tw-relative tw-transition-all tw-duration-200 tw-ease-out tw-flex tw-items-center tw-justify-center",
                            activeTab === "trending" ? "tw-text-[#7927D7]" : "tw-text-[#8B8B8B]"
                        )}
                        style={tabButtonStyles}
                        onClick={() => {
                            setActiveTab("trending");
                            setSocialState({
                                ...socialState,
                                activeTab: "trending",
                            });
                            ScrollBackToTop();
                        }}
                        id="trending-tab-btn"
                        data-tooltip-id="trending-tooltip"
                    >
                        TRENDING
                        {activeTab === "trending" && (
                            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-h-[3px] tw-bg-[#7927D7]" />
                        )}
                    </button>
                    <button
                        className={clsx(
                            "tw-z-1 tw-flex-1 tw-text-center tw-font-bold tw-bg-white tw-relative tw-transition-all tw-duration-200 tw-ease-out tw-flex tw-items-center tw-justify-center",
                            activeTab === "following" ? "tw-text-[#7927D7]" : "tw-text-[#8B8B8B]"
                        )}
                        style={tabButtonStyles}
                        onClick={() => {
                            setActiveTab("following");
                            setSocialState({
                                ...socialState,
                                activeTab: "following",
                            });
                            setActiveTooltip(null);
                            ScrollBackToTop();
                        }}
                        data-tooltip-id="following-tooltip"
                    >
                        <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2">
                            FOLLOWING
                            <FeedTabIconButton
                                hasFeeds={hasFeeds}
                                activeTab={activeTab}
                                scrollProgress={scrollProgress}
                                setActiveTab={setActiveTab}
                                openDrawer={() => setShowFeedDrawer(true)}
                            />
                        </span>
                        {activeTab === "following" && (
                            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-h-[3px] tw-bg-[#7927D7]" />
                        )}
                    </button>
                </div>
                {activeTooltip === "trending" && (
                    <CustomTooltip
                        id="trending-tooltip"
                        placement="bottom"
                        open
                        onClose={handleTrendingTooltipClose}
                        autoCloseTime={5000}
                        animate
                        className="!tw-text-white"
                        style={{ "--tooltip-bg": "#6C26C4" }}
                        customContent={
                            <span>
                                <b>Trending:</b> The most popular listings that everyone's talking about
                            </span>
                        }
                    />
                )}
                {activeTooltip === "following" && (
                    <CustomTooltip
                        id="following-tooltip"
                        placement="bottom"
                        open
                        onClose={handleFollowingTooltipClose}
                        autoCloseTime={5000}
                        animate
                        className="!tw-text-white"
                        style={{ "--tooltip-bg": "#6C26C4" }}
                        customContent={
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <div>
                                    <b>Make PriceMe yours</b>
                                    <br />
                                    Follow listings in the locations and styles you care about
                                </div>
                            </div>
                        }
                    />
                )}
                {/* Tab Content */}
                {activeTab === "trending" ? (
                    <>
                        {/* Feed Properties */}
                        {isLoading ? (
                            // Show skeleton loaders for feed properties when loading
                            <div className="tw-flex tw-flex-col tw-gap-4">
                                {/* If top property is loaded, show it first, then skeletons */}
                                {topPropertyData?.data && plc ? (
                                    <>
                                        <div ref={firstCardRef}>
                                            <SinglePropertyFeed
                                                property={topPropertyData.data}
                                                feedType="trending"
                                            />
                                            {activeTooltip === "see-more" && (
                                                <CustomTooltip
                                                    id="see-more-tooltip"
                                                    open
                                                    placement="top-end"
                                                    onClose={handleSeeMoreTooltipClose}
                                                    autoCloseTime={5000}
                                                    animate
                                                    className="!tw-text-white !tw-z-[2000]"
                                                    style={{ "--tooltip-bg": "#6C26C4" }}
                                                    customContent={
                                                        <span>
                                                            <b>See more:</b> Tap any home to see photos &amp;
                                                            details.
                                                        </span>
                                                    }
                                                />
                                            )}
                                        </div>
                                        {/* Show skeletons for remaining properties */}
                                        {[...Array(2)].map((_, index) => (
                                            <PropertyFeedSkeleton key={`skeleton-${index}`} />
                                        ))}
                                    </>
                                ) : (
                                    // Show only skeletons if no top property
                                    [...Array(3)].map((_, index) => (
                                        <PropertyFeedSkeleton key={`skeleton-${index}`} />
                                    ))
                                )}
                            </div>
                        ) : (
                            // Show actual properties
                            combinedProperties.map((property, idx) => {
                                if (idx === 0) {
                                    // First card: attach ref and click handler
                                    return (
                                        <div key={property.id} ref={firstCardRef}>
                                            <SinglePropertyFeed property={property} feedType="trending" />
                                            {activeTooltip === "see-more" && (
                                                <CustomTooltip
                                                    id="see-more-tooltip"
                                                    open
                                                    placement="top-end"
                                                    onClose={handleSeeMoreTooltipClose}
                                                    autoCloseTime={5000}
                                                    animate
                                                    className="!tw-text-white !tw-z-[2000]"
                                                    style={{ "--tooltip-bg": "#6C26C4" }}
                                                    customContent={
                                                        <span>
                                                            <b>See more:</b> Tap any home to see photos &amp;
                                                            details.
                                                        </span>
                                                    }
                                                />
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <SinglePropertyFeed
                                        key={property.id}
                                        property={property}
                                        feedType="trending"
                                    />
                                );
                            })
                        )}

                        {hasNextPage && (
                            <div
                                ref={loaderRef}
                                className="tw-flex tw-items-center tw-justify-center tw-py-4"
                            >
                                <MoonLoader size={30} color="#63c19f" />
                            </div>
                        )}
                    </>
                ) : (
                    <CustomFeed show={showFeedDrawer} onHide={() => setShowFeedDrawer(false)} />
                )}
            </div>
            {showSuccessAnimation && <SuccessAnimation show onContinue={hideAnimations} />}
            {showDeleteSuccessAnimation && (
                <DeleteSuccessAnimation
                    show
                    message={deleteMessage}
                    onDone={hideAnimations}
                    onHide={hideAnimations}
                />
            )}
        </>
    );
};

function FeedTabIconButton({ hasFeeds, activeTab, scrollProgress, setActiveTab, openDrawer }) {
    const sharedProps = {
        className: clsx(
            "tw-text-xl tw-cursor-pointer",
            activeTab === "following" ? "tw-text-[#7927D7]" : "tw-text-[#8B8B8B]"
        ),
        style: {
            fontSize: `${1.25 - 0.25 * scrollProgress}rem`,
        },
        onClick: (e) => {
            e.stopPropagation();
            setActiveTab("following");
            openDrawer();
        },
    };
    if (hasFeeds) {
        return <PiPencilSimpleLine {...sharedProps} title="Edit Custom Feed Lists" />;
    } else {
        return <IoMdAddCircle {...sharedProps} title="Show Custom Feed Lists" />;
    }
}

export default ListingFeed;
